import { IS_SAFARI } from "app/appConstants";
import { AppMediaSettings } from "app/appMediaSettings";
import { MprcReactPlayer, MprcReactPlayerRef } from "components/MprcReactPlayer";
import { CustomVideoSlide } from "components/types/lightBox";
import React, { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "yet-another-react-lightbox/styles.css";

////////////////////////////////////////////////////////////////////////////////

const useVideoStyles = makeStyles()(
    (theme, p, classes) => ({
        playerWrapper: {
            position: "relative",
            width: "100%",
            height: "calc(100% - 64px)",
            marginTop: "32px",
            //paddingTop: "56.25%" /* 720 / 1280 = 0.5625 */
        },

        reactPlayer: {
            position: "absolute",
            top: 0,
            left: 0,
        }
    })
);

////////////////////////////////////////////////////////////////////////////////

type CustomVideoSlideProps = {
    slide: CustomVideoSlide;
    offset: number;
    onPlaying?: (playing: boolean) => void;
};

const CustomVideoSlideRenderComponent = ({ slide, offset, onPlaying }: CustomVideoSlideProps) => {
    const { id, videoId, url, autoPlay, poster, mimetype, isAudio, isHls, tracks, mediaSessionTitle: videoTitle } = slide;
    const { classes } = useVideoStyles();
    const videoRef = React.useRef<MprcReactPlayerRef>(null!);
    const [playing, setPlay] = useState<boolean>((autoPlay ?? false));
    const [userPlay, setUserPlay] = useState<boolean>((autoPlay ?? false));

    const handleCloseCaptions = (ccLang?: string) => {
        AppMediaSettings.setCloseCaptionsLang(ccLang);
    };

    useEffect(() => {
        if (videoRef.current) {
            if (offset !== 0) {
                videoRef.current.pause();
                setPlay(false);
            }
            else if (offset === 0 && (autoPlay || userPlay)) {
                setPlay(true);
            }
        }
    }, [offset, videoRef.current]);

    const sourceUrls = useMemo(() => {
        //return isHls || !mimetype
        //    ? url
        //    : [{ src: url, type: mimetype }];
        return url;
    }, [url, mimetype]);

    //const id = useRef(new Date().getTime().toString());

    return (
        <div className={classes.playerWrapper}>
            <MprcReactPlayer
                id={id}
                videoId={videoId}
                autoPlay={false}
                playing={playing && userPlay}
                title={videoTitle}
                url={sourceUrls}
                width="100%"
                height="100%"
                className={classes.reactPlayer}
                ref={videoRef}
                autoCloseCaptionLang={AppMediaSettings.getCloseCaptionsLang()}
                onPlay={() => {
                    setUserPlay(true);
                    if (onPlaying) onPlaying(true);
                }}
                onPause={() => {
                    setUserPlay(false);
                    if (onPlaying) onPlaying(false);
                }}
                onEnded={() => {
                    setUserPlay(false);
                    setPlay(false);
                    if (onPlaying) onPlaying(false);
                }}
                onCCChange={handleCloseCaptions}
                config={{
                    file: {
                        attributes: {
                            poster: poster,
                            width: "100%",
                            height: "100%",
                            disablePictureInPicture: true,
                            controlsList: "noplaybackrate nodownload",
                            crossOrigin: "anonymous"
                        },
                        forceAudio: isAudio,
                        forceHLS: isHls && !IS_SAFARI,
                        hlsOptions: {
                        },
                        tracks: tracks ?? []
                    }
                }}
            />
        </div>
    );
};

export const CustomVideoSlideRender = React.memo(
    CustomVideoSlideRenderComponent,
    (prevPops, nextProps) => {
        return prevPops.offset == nextProps.offset && prevPops.slide.url == nextProps.slide.url;
    }
);
